/** Decimal places a money amount may carry. */
export const MONEY_INPUT_MAX_DECIMALS = 2;

/**
 * Filter applied on every keystroke.
 *
 * `inputMode="decimal"` only changes the on-screen keyboard; it does not stop
 * anyone typing letters or pasting `1e9`. This is the actual filter.
 *
 * Rules:
 * - digits, `.` and `,` survive; everything else is dropped
 * - `,` becomes `.`, so a comma keypad (pt-BR, some es-MX Androids) works
 * - the FIRST separator is the decimal point; later ones are dropped
 * - at most `maxDecimals` decimals, truncated rather than rounded — rounding
 *   would silently change the amount someone typed
 *
 * Deliberately NO thousands-separator detection here. That needs a rule like
 * "three digits after a lone separator means grouping", which fights the
 * customer mid-word: typing `10.999` would jump the field to `10999` between
 * keystrokes. Grouping only shows up in pasted text, so it is handled by
 * `normalizePastedMoney` instead.
 *
 * Intermediate states stay valid on purpose (`''`, `'0.'`, `'.'`): deciding
 * whether the settled value is acceptable is the caller's job. Bounds are not
 * handled here either — the minimum depends on the currency (Stripe rejects
 * under MXN $10 / USD $0.50) and belongs to whoever owns the flow.
 */
export function sanitizeMoneyInput(
  raw: string,
  maxDecimals: number = MONEY_INPUT_MAX_DECIMALS,
): string {
  const cleaned = raw.replace(/,/g, '.').replace(/[^\d.]/g, '');

  const firstSeparator = cleaned.indexOf('.');
  if (firstSeparator === -1) return cleaned;

  const whole = cleaned.slice(0, firstSeparator);
  const decimals = cleaned
    .slice(firstSeparator + 1)
    .replace(/\./g, '')
    .slice(0, maxDecimals);

  return `${whole}.${decimals}`;
}

/**
 * Strips a trailing decimal separator once a field SETTLES (loses focus).
 * Pairs with `sanitizeMoneyInput`, which runs on every keystroke and cannot
 * do this job itself — seeing the whole picture is exactly what it does not
 * have.
 *
 * `maxDecimals === 0` — RC-10's whole-amounts rule — is why this exists.
 * While typing, `sanitizeMoneyInput(raw, 0)` already discards every decimal
 * digit correctly (`.slice(0, 0)` is always `''`), but it does so by keeping
 * the separator itself as the boundary: "12.50" reads "12." through every
 * intermediate keystroke, because WITHOUT that boundary the very next digit
 * would have nothing telling it "you are a decimal, be discarded" and would
 * simply concatenate onto the whole part instead — turning "12.50" into
 * "1250", the exact trap RC-10 forbids (verified: an early version that made
 * `sanitizeMoneyInput` drop the separator outright, on every keystroke,
 * reproduced precisely that). So the dangling separator is a REQUIRED
 * mid-typing state for zero decimals, not a bug — see `sanitizeMoneyInput`'s
 * docblock, which already calls intermediate states like `'0.'` and `'.'`
 * acceptable. What is never acceptable is a SETTLED "12.": it looks like a
 * full field while `customAmountToMicros` parses it to no amount at all
 * (its regex requires a digit after the dot). This function is the one
 * place that distinction is enforced, and only there — it runs on blur, once
 * typing is actually done, never mid-keystroke.
 *
 * A caller with `maxDecimals > 0` is left untouched: "12." there is a
 * legitimate partial amount (a customer who has not typed cents yet), and
 * this is not the layer that decides whether an incomplete decimal amount is
 * valid — that belongs to whoever owns the flow, same as `sanitizeMoneyInput`
 * never touches bounds.
 */
export function settleMoneyInput(
  value: string,
  maxDecimals: number = MONEY_INPUT_MAX_DECIMALS,
): string {
  if (maxDecimals !== 0) return value;

  return value.endsWith('.') ? value.slice(0, -1) : value;
}

/**
 * Interprets pasted text, where thousands separators are common and getting
 * them wrong changes the amount by a factor of a thousand.
 *
 * Which separator is the decimal one:
 * - both kinds present -> the LAST one is decimal, the other groups thousands
 *   (`1,234.56` and `1.234,56` both mean 1234.56)
 * - one kind, repeated -> grouping (`1,234,567`)
 * - one kind, once, followed by exactly three digits -> grouping (`1,234`).
 *   This is the genuinely ambiguous case; grouping is the far more common
 *   intent in pasted text, and reading it as decimals would divide by a
 *   thousand — the more expensive mistake of the two.
 * - anything else -> decimal (`12,50`, `12.5`)
 *
 * The result still goes through `sanitizeMoneyInput`, so decimal limits and
 * character filtering stay in one place.
 */
export function normalizePastedMoney(
  raw: string,
  maxDecimals: number = MONEY_INPUT_MAX_DECIMALS,
): string {
  const cleaned = raw.replace(/[^\d.,]/g, '');
  if (cleaned === '') return '';

  const lastDot = cleaned.lastIndexOf('.');
  const lastComma = cleaned.lastIndexOf(',');
  const lastSeparator = Math.max(lastDot, lastComma);
  if (lastSeparator === -1) return cleaned;

  const hasBoth = lastDot !== -1 && lastComma !== -1;
  const separator = lastSeparator === lastDot ? '.' : ',';
  const occurrences = cleaned.split(separator).length - 1;
  const digitsAfter = cleaned.length - lastSeparator - 1;

  const isGrouping =
    !hasBoth && (occurrences > 1 || (digitsAfter === 3 && cleaned.length > 4));

  if (isGrouping) return cleaned.replace(/[.,]/g, '');

  const whole = cleaned.slice(0, lastSeparator).replace(/[.,]/g, '');
  const decimals = cleaned.slice(lastSeparator + 1).replace(/[.,]/g, '');

  /*
    Se ASIENTA, además de sanearse. Un pegado no es un estado intermedio: el
    valor entero llega de golpe y ya está completo, así que la razón por la que
    `sanitizeMoneyInput` tiene que conservar el separador colgante mientras se
    teclea —marcar la frontera para que el siguiente dígito no se concatene al
    entero— aquí no aplica: no hay siguiente dígito.

    Sin esto, pegar "12.50" con cero decimales dejaba "12." asentado en el
    campo, y el pie del modal compone el total con ese texto crudo: el cliente
    leía "$12. USD" junto al botón de pagar, y Continuar le contestaba que
    escribiera un monto válido. El tecleo ya estaba cubierto por el blur; pegar
    no pasa por blur.
  */
  return settleMoneyInput(
    sanitizeMoneyInput(`${whole}.${decimals}`, maxDecimals),
    maxDecimals,
  );
}
